import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { PlusIcon, TrashIcon, PencilIcon, KeyIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { createKeyTypeAsync, updateKeyTypeAsync, deleteKeyTypeAsync } from '../store/keyTypesSlice';
import { KeyType } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

interface KeyTypeFormData {
  name: string;
  description: string;
}

export default function KeyTypes() {
  const dispatch = useAppDispatch();
  const { keyTypes, loading } = useAppSelector((state) => state.keyTypes);
  const currentUser = useAppSelector((state) => state.users.currentUser);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKeyType, setEditingKeyType] = useState<KeyType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { register, handleSubmit, reset, setValue } = useForm<KeyTypeFormData>();

  const handleOpenModal = (keyType?: KeyType) => {
    if (keyType) {
      setEditingKeyType(keyType);
      setValue('name', keyType.name);
      setValue('description', keyType.description || '');
    } else {
      setEditingKeyType(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKeyType(null);
    reset();
  };

  const onSubmit = async (data: KeyTypeFormData) => {
    setIsSaving(true);

    try {
      if (editingKeyType) {
        const updatedKeyType: KeyType = {
          ...editingKeyType,
          name: data.name,
          description: data.description || undefined,
        };
        await dispatch(updateKeyTypeAsync(updatedKeyType)).unwrap();
      } else {
        const newKeyType: KeyType = {
          id: `kt-${Date.now()}`,
          name: data.name,
          description: data.description || undefined,
        };
        await dispatch(createKeyTypeAsync(newKeyType)).unwrap();
      }

      handleCloseModal();
    } catch (error) {
      console.error('Failed to save key type:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (keyType: KeyType) => {
    if (confirm(`Are you sure you want to delete "${keyType.name}"?`)) {
      try {
        await dispatch(deleteKeyTypeAsync(keyType.id)).unwrap();
      } catch (error) {
        console.error('Failed to delete key type:', error);
        alert('Failed to delete. Please try again.');
      }
    }
  };

  // Filter key types based on search
  const filteredKeyTypes = keyTypes.filter((kt) =>
    kt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort alphabetically by name
  const sortedKeyTypes = [...filteredKeyTypes].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-600 dark:text-secondary-400 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Key Types</h1>
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              Manage the types of keys in your system ({keyTypes.length} total)
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenModal()} disabled={loading}>
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Key Type
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            Search Key Types
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name..."
            className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
        </div>
      )}

      {/* Key Types Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-secondary-200 dark:border-secondary-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                  Description
                </th>
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {sortedKeyTypes.length > 0 ? (
                sortedKeyTypes.map((keyType) => (
                  <tr key={keyType.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 p-1.5 rounded bg-primary-100 dark:bg-primary-900/30 mr-3">
                          <KeyIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                          {keyType.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-400">
                      {keyType.description || '-'}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenModal(keyType)}
                            className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                            title="Edit"
                            disabled={loading}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(keyType)}
                            className="p-1 text-danger hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Delete"
                            disabled={loading}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 3 : 2} className="px-4 py-8 text-center text-secondary-500 dark:text-secondary-400">
                    {searchTerm ? 'No key types match your search' : 'No key types found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {keyTypes.length === 0 && !loading && !searchTerm && (
        <Card>
          <div className="py-12 text-center">
            <KeyIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">No key types</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Get started by adding a new key type.
            </p>
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingKeyType ? 'Edit Key Type' : 'Add Key Type'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingKeyType ? 'Update' : 'Add'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Key Type Name *
            </label>
            <input
              type="text"
              {...register('name', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., AAA 5"
              disabled={isSaving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional description for this key type"
              disabled={isSaving}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
